import { getDeviceContext } from './core/device';
import { collectDefaultPage, getPageContext, setPageContext } from './core/page';
import { EventQueue } from './core/queue';
import {
  getUserContext,
  identifyUser,
  resetUserIdentity,
  setUserTraits,
  touchSession,
} from './core/user';
import { createId, isBrowser, logDebug, sanitizeProperties } from './utils';
import { SDK_VERSION } from './version';
import type {
  ExposeParams,
  PageLeaveParams,
  PageViewParams,
  TrackOptions,
  TrackingClient,
  TrackingInitOptions,
  TrackingPayload,
} from './types';

const DEFAULTS: Required<
  Pick<
    TrackingInitOptions,
    'debug' | 'batchSize' | 'flushIntervalMs' | 'autoFlushOnUnload' | 'maxRetries'
  >
> = {
  debug: false,
  batchSize: 10,
  flushIntervalMs: 5000,
  autoFlushOnUnload: true,
  maxRetries: 2,
};

function assertBrowser(debug: boolean): boolean {
  if (isBrowser()) return true;
  logDebug(debug, 'skip: not in browser');
  return false;
}

export function createTracking(initOptions: TrackingInitOptions): TrackingClient {
  let options: TrackingInitOptions & typeof DEFAULTS = {
    ...DEFAULTS,
    ...initOptions,
  };

  if (!options.productId) {
    throw new Error('[tracking-sdk] productId is required');
  }
  if (!options.endpoint) {
    throw new Error('[tracking-sdk] endpoint is required');
  }

  let sequenceId = 0;
  let ready = false;
  const exposedKeys = new Set<string>();
  const observers = new Map<Element, IntersectionObserver>();

  const queue = new EventQueue(
    {
      endpoint: options.endpoint,
      productId: options.productId,
      environment: options.environment,
      debug: options.debug,
      maxRetries: options.maxRetries,
    },
    options.batchSize,
    options.flushIntervalMs
  );

  const unloadHandler = () => {
    void queue.flush(true);
  };

  const visibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      void queue.flush(true);
    }
  };

  function bindUnload(): void {
    if (!options.autoFlushOnUnload || !isBrowser()) return;
    window.addEventListener('pagehide', unloadHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  function unbindUnload(): void {
    if (!isBrowser()) return;
    window.removeEventListener('pagehide', unloadHandler);
    document.removeEventListener('visibilitychange', visibilityHandler);
  }

  function buildPayload(
    eventName: string,
    properties: Record<string, unknown>,
    eventVersion = '1.0'
  ): TrackingPayload {
    touchSession();
    sequenceId += 1;
    return {
      event_id: createId('evt'),
      event_name: eventName,
      event_version: eventVersion,
      event_time: Date.now(),
      sequence_id: sequenceId,
      product_id: options.productId,
      project_id: options.projectId,
      environment: options.environment,
      app_version: options.appVersion,
      sdk_version: SDK_VERSION,
      platform: 'web',
      user: getUserContext(),
      page: getPageContext(),
      device: getDeviceContext(),
      properties: sanitizeProperties(properties),
    };
  }

  function enqueue(eventName: string, properties: Record<string, unknown>, opts?: TrackOptions): void {
    if (!ready) {
      logDebug(options.debug, 'not initialized, auto init');
      client.init();
    }
    if (!assertBrowser(options.debug)) return;

    const payload = buildPayload(eventName, properties, opts?.eventVersion);
    logDebug(options.debug, 'enqueue', payload.event_name, payload);
    queue.enqueue(payload, {
      immediate: Boolean(opts?.immediate),
      preferBeacon: Boolean(opts?.preferBeacon),
    });
  }

  const client: TrackingClient = {
    init(partial) {
      if (partial) {
        options = { ...options, ...partial };
        if (!options.productId || !options.endpoint) {
          throw new Error('[tracking-sdk] productId and endpoint are required');
        }
        queue.updateTransport({
          endpoint: options.endpoint,
          productId: options.productId,
          environment: options.environment,
          debug: options.debug,
          maxRetries: options.maxRetries,
        });
        queue.updateConfig(options.batchSize, options.flushIntervalMs);
      }

      if (!assertBrowser(options.debug)) {
        ready = true;
        return;
      }

      if (!ready) {
        getDeviceContext();
        setPageContext(collectDefaultPage());
        queue.start();
        bindUnload();
        ready = true;
        logDebug(options.debug, 'initialized', {
          productId: options.productId,
          environment: options.environment,
          endpoint: options.endpoint,
          sdk: SDK_VERSION,
        });
      }
    },

    track(eventName: string, properties: Record<string, unknown> = {}, optionsArg?: TrackOptions) {
      enqueue(eventName, properties, optionsArg);
    },

    pageView(params: PageViewParams, optionsArg?: TrackOptions) {
      setPageContext({
        page_id: params.page_id,
        page_name: params.page_name,
        route_name: params.route_name,
        page_url: params.page_url,
        referrer_url: params.referrer_url,
        entry_source: params.entry_source,
      });
      enqueue(
        'page_view',
        {
          page_id: params.page_id,
          page_name: params.page_name,
          route_name: params.route_name,
          entry_source: params.entry_source,
        },
        optionsArg
      );
    },

    pageLeave(params: PageLeaveParams, optionsArg?: TrackOptions) {
      const leaveType = params.leave_type || 'route_change';
      const unloadLike = leaveType === 'close' || leaveType === 'hide';
      enqueue(
        'page_leave',
        {
          page_id: params.page_id,
          duration_ms: params.duration_ms,
          leave_type: leaveType,
          route_name: params.route_name,
        },
        {
          ...optionsArg,
          // close / hide 默认即时发送；close 优先 beacon，hide 走 fetch 便于观察与送达
          immediate: optionsArg?.immediate ?? unloadLike,
          preferBeacon: optionsArg?.preferBeacon ?? leaveType === 'close',
        }
      );
    },

    expose(element: Element, params: ExposeParams, optionsArg?: TrackOptions) {
      if (!assertBrowser(options.debug)) return () => undefined;

      const key = `${params.element_id}:${params.content_id ?? ''}:${params.position ?? ''}`;
      const cleanup = () => {
        const obs = observers.get(element);
        if (obs) {
          obs.disconnect();
          observers.delete(element);
        }
      };

      if (typeof IntersectionObserver === 'undefined') {
        if (!exposedKeys.has(key)) {
          exposedKeys.add(key);
          enqueue('element_expose', { ...params }, optionsArg);
        }
        return cleanup;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            if (entry.intersectionRatio < 0.5) continue;
            if (exposedKeys.has(key)) {
              cleanup();
              return;
            }
            exposedKeys.add(key);
            enqueue('element_expose', { ...params }, optionsArg);
            cleanup();
          }
        },
        { threshold: [0.5] }
      );

      observer.observe(element);
      observers.set(element, observer);
      return cleanup;
    },

    identify(userId, traits) {
      identifyUser(userId, traits ? sanitizeProperties(traits as Record<string, unknown>) : undefined);
      logDebug(options.debug, 'identify', userId, traits);
    },

    resetIdentity() {
      resetUserIdentity();
      exposedKeys.clear();
      logDebug(options.debug, 'resetIdentity');
    },

    setUserProperties(traits) {
      setUserTraits(sanitizeProperties(traits));
      logDebug(options.debug, 'setUserProperties', traits);
    },

    async flush() {
      await queue.flush(false);
    },

    getQueueSize() {
      return queue.size();
    },

    destroy() {
      unbindUnload();
      queue.stop();
      observers.forEach((obs) => obs.disconnect());
      observers.clear();
      ready = false;
    },
  };

  // 创建即完成初始化（浏览器侧）
  client.init();
  return client;
}
